import { Decimal } from 'decimal.js';
import { FormulaDecimal } from './decimal.js';
import { describeValue, truthy } from './values.js';
export { truthy };
export { FormulaDecimal };
/** 2^96, the first magnitude System.Decimal cannot hold; from text, since an operation would round it to 28 digits. */
const DECIMAL_LIMIT = new FormulaDecimal('79228162514264337593543950336');
/**
 * An operation's result as the language has it (§4.4): outside System.Decimal it is an error, never a value — the
 * C# back-ends cannot hold it and throw, so this one throws too; and zero has no sign, as C# composes it, so a
 * grid never shows "−0" where the server stores 0.
 */
function settle(x) {
    if (x.abs().gte(DECIMAL_LIMIT))
        throw new RangeError('The formula result is outside the range of System.Decimal.');
    return x.isZero() && x.isNegative() ? x.neg() : x;
}
/** A number literal as written in the formula (`1.50` keeps its scale). */
export function num(text) {
    return new FormulaDecimal(text);
}
const NUMERIC_TEXT = /^-?\d+(\.\d+)?$/;
/**
 * A host's numeric input — a grid cell, a JSON number, text typed into an edit cell — as the formula's number:
 * null for null, undefined, blank or unparseable text, and for a non-finite number. A generated row function
 * supplies a required field's default after this (`F.input(row.quantity) ?? F.num("0")`), as the server does
 * when a required number is missing from the payload.
 */
export function input(value) {
    if (value === null || value === undefined)
        return null;
    if (value instanceof FormulaDecimal)
        return value;
    if (typeof value === 'number')
        return Number.isFinite(value) ? new FormulaDecimal(value) : null;
    if (typeof value === 'string')
        return NUMERIC_TEXT.test(value.trim()) ? new FormulaDecimal(value.trim()) : null;
    return null;
}
export function toNumber(x) {
    return x === null ? null : x.toNumber();
}
export function add(a, b) {
    return a === null || b === null ? null : settle(a.plus(b));
}
export function sub(a, b) {
    return a === null || b === null ? null : settle(a.minus(b));
}
export function mul(a, b) {
    return a === null || b === null ? null : settle(a.times(b));
}
/** Null when `b` is zero (§4.4), so a division is always possibly null. */
export function div(a, b) {
    return a === null || b === null || b.isZero() ? null : settle(a.div(b));
}
export function neg(a) {
    return a === null ? null : settle(a.neg());
}
export function abs(a) {
    return a === null ? null : a.abs();
}
export function min(a, b, where) {
    if (isRowArray(a))
        return extreme(a, b, where, (candidate, current) => candidate.lt(current));
    return a === null || b === null ? null : FormulaDecimal.min(a, b);
}
export function max(a, b, where) {
    if (isRowArray(a))
        return extreme(a, b, where, (candidate, current) => candidate.gt(current));
    return a === null || b === null ? null : FormulaDecimal.max(a, b);
}
function isRowArray(value) {
    return Array.isArray(value);
}
function kept(rows, where) {
    return where ? rows.filter(where) : rows;
}
/** `sum(coll, expr [, where])`: the total of the non-null values, added in row order at 28 significant digits (each addition held to System.Decimal's range); 0 when nothing is left. */
export function sum(rows, value, where) {
    let total = new FormulaDecimal(0);
    for (const row of kept(rows, where)) {
        const v = value(row);
        if (v !== null)
            total = add(total, v);
    }
    return total;
}
/** `count(coll [, where])`: the number of rows kept; 0 when empty. */
export function count(rows, where) {
    return new FormulaDecimal(kept(rows, where).length);
}
function extreme(rows, value, where, better) {
    let result = null;
    for (const row of kept(rows, where)) {
        const v = value(row);
        if (v !== null && (result === null || better(v, result)))
            result = v;
    }
    return result;
}
/** Saved ⊕ tracked for `min` (Computed-Fields.md §8.3): the smaller of two partial extremes, where null means "no rows". */
export function combineMin(a, b) {
    return a === null ? b : b === null ? a : FormulaDecimal.min(a, b);
}
/** Saved ⊕ tracked for `max`: the larger of two partial extremes, where null means "no rows". */
export function combineMax(a, b) {
    return a === null ? b : b === null ? a : FormulaDecimal.max(a, b);
}
/** `any(coll, pred)`: true when some row satisfies the predicate; false when empty. */
export function any(rows, predicate) {
    return rows.some(predicate);
}
/** `all(coll, pred)`: true when every row satisfies the predicate; true when empty (FL-W01 warns where that is unguarded). */
export function all(rows, predicate) {
    return rows.every(predicate);
}
export function round(x, decimals, mode) {
    if (x === null)
        return null;
    return settle(x.toDecimalPlaces(decimals, mode === 'halfUp' ? Decimal.ROUND_HALF_UP : Decimal.ROUND_HALF_EVEN));
}
/** Ordered or equality comparison of two values of one kind; null when either is null. */
export function compare(op, a, b) {
    if (a === null || b === null)
        return null;
    const order = orderOf(a, b);
    switch (op) {
        case '<':
            return order < 0;
        case '<=':
            return order <= 0;
        case '>':
            return order > 0;
        case '>=':
            return order >= 0;
        case '==':
            return order === 0;
        default:
            return order !== 0;
    }
}
export function lt(a, b) {
    return compare('<', a, b);
}
export function le(a, b) {
    return compare('<=', a, b);
}
export function gt(a, b) {
    return compare('>', a, b);
}
export function ge(a, b) {
    return compare('>=', a, b);
}
export function eq(a, b) {
    return compare('==', a, b);
}
export function ne(a, b) {
    return compare('!=', a, b);
}
/** -1, 0 or 1 for two non-null values of one runtime kind (the checker guarantees the kinds match). */
function orderOf(a, b) {
    if (a instanceof FormulaDecimal && b instanceof FormulaDecimal)
        return a.cmp(b);
    if (a instanceof Date && b instanceof Date)
        return Math.sign(a.getTime() - b.getTime());
    if (typeof a === 'string' && typeof b === 'string')
        return orderOfStrings(a, b);
    if (typeof a === 'boolean' && typeof b === 'boolean')
        return orderOfBooleans(a, b);
    throw new Error(`Cannot compare ${describeValue(a)} with ${describeValue(b)}.`);
}
/** Ordinal, as the C# back-end compares; only equality reaches here for strings, the checker refuses ordering them. */
function orderOfStrings(a, b) {
    if (a === b)
        return 0;
    return a < b ? -1 : 1;
}
function orderOfBooleans(a, b) {
    if (a === b)
        return 0;
    return a ? 1 : -1;
}
// ── dates (§4.7): calendar values with no time zone, held in a Date's UTC fields ─────────────
const MS_PER_DAY = 86400000;
/** The most months C#'s DateTime.AddMonths takes; beyond it both back-ends refuse rather than wrap. */
const MAX_MONTHS = 120000;
/** The whole number of days or months a call adds; a checked tree passes only an Integer. */
function wholeNumber(n, fn) {
    if (!n.isInteger())
        throw new RangeError(`${fn}() adds a whole number; got ${n.toString()}.`);
    return n.toNumber();
}
/** A result inside the years 1–9999, the range C#'s DateTime holds: outside it both back-ends fail, neither returns a value. */
function inDateRange(date, fn) {
    const year = date.getUTCFullYear();
    if (Number.isNaN(date.getTime()) || year < 1 || year > 9999)
        throw new RangeError(`${fn}() leaves the years 1–9999.`);
    return date;
}
export function addDays(d, n) {
    if (d === null || n === null)
        return null;
    return inDateRange(new Date(d.getTime() + wholeNumber(n, 'addDays') * MS_PER_DAY), 'addDays');
}
export function addMonths(d, n) {
    if (d === null || n === null)
        return null;
    const months = wholeNumber(n, 'addMonths');
    if (Math.abs(months) > MAX_MONTHS)
        throw new RangeError(`addMonths() adds at most ${MAX_MONTHS} months; got ${months}.`);
    const total = d.getUTCFullYear() * 12 + d.getUTCMonth() + months;
    const year = Math.floor(total / 12);
    const month = total - year * 12;
    const result = new Date(d.getTime());
    // setUTCFullYear, not Date.UTC: Date.UTC reads a year below 100 as 19xx.
    result.setUTCFullYear(year, month, Math.min(d.getUTCDate(), daysInMonth(year, month)));
    return inDateRange(result, 'addMonths');
}
function daysInMonth(year, month) {
    const lastDay = new Date(0);
    lastDay.setUTCFullYear(year, month + 1, 0);
    return lastDay.getUTCDate();
}
const DATE_TEXT = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?)?(Z|[+-]\d{2}:?\d{2})?$/;
/**
 * A host's date — text as a form or a DTO holds it, or a Date a picker produced — as the calendar value a formula
 * computes with (a Date whose UTC fields are that calendar date and time, as the conformance suite reads one). A
 * `date` is its written year, month and day: a date has no time zone, so "2026-10-04T00:00:00Z" is 4 October
 * wherever the browser is. A `datetime` is the wall-clock time the user sees: text without a zone as written,
 * an instant (a zone, or a Date object) in the browser's time zone — the time a form shows and the server's
 * DateTime holds. Null for null, undefined, blank or unparseable input.
 */
export function inputDate(value, kind) {
    if (value === null || value === undefined)
        return null;
    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? null : calendarOf(localFields(value), kind);
    if (typeof value !== 'string')
        return null;
    const match = DATE_TEXT.exec(value.trim());
    if (!match)
        return null;
    const [, y, mo, d, h = '0', mi = '0', s = '0', fraction = '0', zone] = match;
    if (kind === 'date' || !zone) {
        return calendarOf([Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s), Number(fraction.slice(0, 3).padEnd(3, '0'))], kind);
    }
    const instant = new Date(value.trim());
    return Number.isNaN(instant.getTime()) ? null : calendarOf(localFields(instant), kind);
}
/**
 * A calendar value back as the host holds it: a `date` as "yyyy-MM-dd"; a `datetime` as the ISO instant of that
 * wall-clock time in the browser's time zone, which is how a form holds a date-time field.
 */
export function toDateText(value, kind) {
    if (kind === 'date') {
        return `${String(value.getUTCFullYear()).padStart(4, '0')}-${String(value.getUTCMonth() + 1).padStart(2, '0')}-${String(value.getUTCDate()).padStart(2, '0')}`;
    }
    const local = new Date(0);
    local.setFullYear(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate());
    local.setHours(value.getUTCHours(), value.getUTCMinutes(), value.getUTCSeconds(), value.getUTCMilliseconds());
    return local.toISOString();
}
function localFields(date) {
    return [date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()];
}
function calendarOf([y, mo, d, h, mi, s, ms], kind) {
    const date = new Date(0);
    date.setUTCFullYear(y, mo, d);
    if (kind === 'datetime')
        date.setUTCHours(h, mi, s, ms);
    // A day the month does not have ("2026-02-30") rolls over in Date; it is not a date.
    return date.getUTCFullYear() === y && date.getUTCMonth() === mo && date.getUTCDate() === d ? date : null;
}
